<template>
  <div v-if="hasAccess" class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card border-0" style="height: calc(100vh - 120px); display: flex; flex-direction: column;">
      <div class="card-header py-3 d-flex flex-wrap align-items-center gap-3">
        <h5 class="mb-0">KIMAP List</h5>

        <div class="d-flex flex-wrap gap-2 flex-grow-1 justify-content-center">
          <IconField>
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>

            <InputText
v-model="table.search" type="text" size="small" placeholder="Search" class="rounded-pill"
              style="min-width: 500px;" @keyup="filter" />
          </IconField>
        </div>
      </div>

      <div style="flex: 1; overflow-y: auto;">
        <TreeTable
:value="table.onLoad ? dummyRows : filteredTree" scrollable scroll-height="flex"
          table-style="min-width: 100%; width: max-content;">
          <!-- CODE -->
          <Column field="code" header="Code" sortable header-class="bg-light text-dark" expander style="min-width: 260px;">
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              <span :class="levelClass(slotProps.node.data.level)">{{ slotProps.node.data.code }}</span>
            </template>
          </Column>

          <!-- DESCRIPTION -->
          <Column field="description" header="Description" sortable header-class="bg-light text-dark" style="min-width: 320px;">
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.node.data.description }}
            </template>
          </Column>

          <!-- LEVEL -->
          <Column field="level" header="Level" sortable header-class="bg-light text-dark" style="min-width: 120px;">
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              <span :class="levelBadge(slotProps.node.data.level)">L{{ slotProps.node.data.level }}</span>
            </template>
          </Column>

          <template #empty>
            <div style="text-align: center; padding: 40px 0;">
              <img src="/no_data.png" style="max-width: 330px; opacity: 0.7;" />
              <div style="color:#888;font-weight:500;">
                No Data
              </div>
            </div>
          </template>
        </TreeTable>
      </div>

      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination v-model="table.currentPage" :page-count="1" size="sm" />
        <p>{{ totalRows }} of {{ table.total }}</p>
      </div>
    </div>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import BasePagination from "@/components/BasePagination.vue";
import NoAccess from "@/components/NoAccess.vue";
import api from "@/helpers/api";
import kimapService from "@/services/generalMasterData/kimapService";
import moment from "moment";

export default {
  components: { BasePagination, NoAccess },
  data() {
    return {
      dummyRows: Array.from({ length: 10 }, (_, i) => ({ key: String(i), data: {}, children: [], leaf: true })),
      table: {
        onLoad: true,
        search: "",
        data: [],
        total: 0,
        currentPage: 1,
        loadTimeout: null,
      },
    };
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    hasAccess() {
      return this.authUserPermissions.includes('kimap-view');
    },
    filteredTree() {
      if (!this.table.search.trim()) return this.table.data;
      const q = this.table.search.toLowerCase();
      return this.filterTree(this.table.data, q);
    },
    totalRows() {
      const count = (nodes) =>
        nodes.reduce((sum, n) => sum + 1 + count(n.children || []), 0);
      return count(this.filteredTree);
    },
  },
  mounted() {
    if (this.hasAccess) {
      this.get();
    }
  },
  methods: {
    async get() {
      this.table.onLoad = true;
      try {
        const response = await api(this, kimapService.tree(), false);
        this.table.data = response.data || [];
        const count = (nodes) =>
          nodes.reduce((sum, n) => sum + 1 + count(n.children || []), 0);
        this.table.total = count(this.table.data);
      } catch {
        this.table.data = [];
        this.table.total = 0;
      } finally {
        this.table.onLoad = false;
      }
    },
    filter() {
      clearTimeout(this.table.loadTimeout);
      this.table.loadTimeout = setTimeout(() => {}, 300);
    },
    filterTree(nodes, q) {
      return nodes.reduce((acc, node) => {
        const match =
          (node.data.code || "").toLowerCase().includes(q) ||
          (node.data.description || "").toLowerCase().includes(q);
        const filteredChildren = this.filterTree(node.children || [], q);
        if (match || filteredChildren.length > 0) {
          acc.push({ ...node, children: filteredChildren });
        }
        return acc;
      }, []);
    },
    levelClass(level) {
      return level === 1 ? "fw-bold" : level === 2 ? "fw-semibold" : "";
    },
    levelBadge(level) {
      return level === 1
        ? "badge bg-primary"
        : level === 2
        ? "badge bg-info text-dark"
        : "badge bg-secondary";
    },
    formatDate(date) {
      return moment(date).format("DD MMMM YYYY, HH:mm");
    },
  },
};
</script>
